import { SettingsPermissions } from "@redotech/account-common/permissions";
import { t } from "@redotech/redo-i18n";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { Outlet } from "react-router-dom";
import { TranslatedBreadcrumbLabel } from "../../../../return/returns-breadcrumb-label";

const orderEditingSettingsBreadcrumb = breadcrumbSlot();

export const fulfillmentSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      get breadcrumb() {
        return t("Order management");
      },
      breadcrumbNavigate: false,
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "order-management",
    children: [
      {
        handle: {
          breadcrumb: orderEditingSettingsBreadcrumb,
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "order-editing",
        lazy: async () => {
          const { OrderEditingSettingsPage } =
            await import("../../../../setting/order-editing/order-editing-settings");
          return {
            element: (
              <>
                <TranslatedBreadcrumbLabel
                  breadcrumb={orderEditingSettingsBreadcrumb}
                  getLabel={(t) =>
                    t("Order editing", {
                      context: "merchant settings label for order editing",
                    })
                  }
                />
                <OrderEditingSettingsPage />
              </>
            ),
          };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Order management");
      },
      breadcrumbNavigate: false,
      permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    },
    path: "oms",
    children: [
      {
        handle: {
          get breadcrumb() {
            return t("General", {
              context:
                "merchant settings label for general order fulfillment configuration",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "general",
        lazy: async () => {
          const { GeneralOmsSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/general");
          return { Component: GeneralOmsSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Auto-merge fulfillments", {
              context:
                "merchant settings label for automatically merging fulfillments",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "automerge-shipments",
        lazy: async () => {
          const { AutoMergeSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/auto-merge-shipments");
          return { Component: AutoMergeSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Automations", {
              context:
                "merchant settings label for order fulfillment automations",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "automations",
        children: [
          {
            index: true,
            lazy: async () => {
              const { AutomationsPage: Automations } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/automations/automations");
              return { Component: Automations };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Automation Builder");
              },
              permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
            },
            path: ":automationId",

            async lazy() {
              const { OmsAutomationBuilder } =
                await import("../../../../setting/oms/automations/automation-builder");
              return { Component: OmsAutomationBuilder };
            },
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Custom imports", {
              context:
                "merchant settings label for custom order import schemas",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "custom-import-schemas",
        lazy: async () => {
          const { CustomImportSchemas } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/custom-import-schemas");
          return { Component: CustomImportSchemas };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("International", {
              context:
                "merchant settings label for international order fulfillment",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "international",
        lazy: async () => {
          const { LoadInternationalSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/international");
          return { Component: LoadInternationalSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Packages", {
              context: "merchant settings label for fulfillment packages",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "packages",
        lazy: async () => {
          const { ParcelsPage: Parcels } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/parcels");
          return { Component: Parcels };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Presets", {
              context: "merchant settings label for order fulfillment presets",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "presets",
        lazy: async () => {
          const { PresetsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/presets/presets");
          return { Component: PresetsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Printing", {
              context: "merchant settings label for order fulfillment printing",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "printing",
        element: <Outlet />,
        children: [
          {
            index: true,
            async lazy() {
              const { PrintingSettingsPage } =
                // redont-allow-dynamic-import: route-level code splitting for the carved fulfillment app
                await import("@redotech/redo-merchant-fulfillment-app/setting/printing");
              return { Component: PrintingSettingsPage };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Packing slips");
              },
              permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
            },
            path: "packing-slips",
            children: [
              {
                index: true,
                async lazy() {
                  const { PackingSlipsPage } =
                    // redont-allow-dynamic-import: route-level code splitting for the carved fulfillment app
                    await import("@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slips-page");
                  return { Component: PackingSlipsPage };
                },
              },
              {
                handle: {
                  get breadcrumb() {
                    return t("Packing slip builder");
                  },
                },
                path: ":packingSlipId",
                async lazy() {
                  const { PackingSlipBuilder } =
                    // redont-allow-dynamic-import: route-level code splitting for the carved fulfillment app
                    await import("@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slip-builder");
                  return { Component: PackingSlipBuilder };
                },
              },
            ],
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Reports", {
              context: "merchant settings label for order fulfillment reports",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "reports",
        lazy: async () => {
          const { ReportsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/reports/oms-reports-page");
          return { Component: ReportsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Scanning & warehouse", {
              context:
                "merchant settings label for fulfillment scanning and warehouse tools",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "scanning-warehouse",
        lazy: async () => {
          const { ScanningWarehouseSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/scanning-warehouse");
          return { Component: ScanningWarehouseSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Warehouse dashboard", {
              context:
                "merchant settings label for the fulfillment warehouse dashboard",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "warehouse-dashboard",
        lazy: async () => {
          const { WarehouseDashboardSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/warehouse-dashboard-settings");
          return { Component: WarehouseDashboardSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Security", {
              context: "merchant settings label for fulfillment security",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "security",
        lazy: async () => {
          const { SecuritySettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/security");
          return { Component: SecuritySettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Warehouse builder");
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "warehouse-builder",
        lazy: async () => {
          const { WarehouseBuilderWithProvider } =
            await import("@redotech/redo-merchant-fulfillment-app/wms/warehouse-builder/warehouse-builder");
          return { Component: WarehouseBuilderWithProvider };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Warehouse layouts", {
              context:
                "merchant settings label for fulfillment warehouse layouts",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "warehouse-layouts",
        lazy: async () => {
          const { WarehouseLayoutsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/warehouse-layouts");
          return { Component: WarehouseLayoutsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Work order flows", {
              context:
                "merchant settings label for fulfillment work order flows",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "work-order-flows",
        children: [
          {
            index: true,
            lazy: async () => {
              const { WorkOrderFlowsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/work-order-flows/work-order-flows");
              return { Component: WorkOrderFlowsPage };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Flow Builder", {
                  context:
                    "merchant settings breadcrumb for the fulfillment work order flow builder",
                });
              },
              permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
            },
            path: ":flowId",
            async lazy() {
              const { WorkOrderFlowBuilder } =
                await import("../../../../setting/oms/work-order-flows/work-order-flow-builder");
              return { Component: WorkOrderFlowBuilder };
            },
          },
        ],
      },
    ],
  },
];
